
import { fetchPlanningOrders } from '@/entities/orders/thunks/fetchPlanningOrders'
import { fetchPlanningOrdersByShift } from '@/entities/orders/thunks/fetchPlanningOrdersByShift'
import { fetchRunsheetOrdersByShift } from '@/entities/orders/thunks/fetchRunsheetOrdersByShift'
import { fetchUnassignedOrdersByRunsheet } from '@/entities/orders/thunks/fetchUnassignedOrdersByRunsheet'
import { fetchUnassignedOrdersByScenario } from '@/entities/orders/thunks/fetchUnassignedOrdersByScenario'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { AppDispatch, RootState } from '@/shared/store'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { fetchHistoricalRunsheets } from './fetchHistoricalRunsheets'
import { fetchRunsheets } from './fetchRunsheets'
import { fetchScenarios } from './fetchScenarios'

export type RefreshDataPayload = {
  additionalHistoricalRunsheetDates?: string[] // Additional historical runsheet dates to pull
}

export const refreshData = createAsyncThunk<void, RefreshDataPayload, { state: RootState, dispatch: AppDispatch }>(
  'schedule/refreshData',
  async ({ additionalHistoricalRunsheetDates }, { dispatch, getState }) => {
    const state = getState()
    const currentScreen = state.userInterface.currentScreen
    const activeWidgetWindows = widgetWindowsSelector.selectAll(state).filter(window => window.isActive)
    const dispatchPromises = []
    const historicalRunsheetDates: Set<string> = new Set(additionalHistoricalRunsheetDates || [])

    // Refresh the runsheets and scenarios data accordingly
    if (currentScreen === 'planning') {
      await dispatch(fetchScenarios({ scenarioIds: [] }))

      // Refresh active widget windows' data
      for (const window of activeWidgetWindows) {
        if (window.widgetType === TREE_WIDGETS.PlanningShiftTable) {
          dispatchPromises.push(dispatch(fetchPlanningOrdersByShift({ shiftId: window.shiftId, forceRefresh: true })))
        } else if (window.widgetType === TREE_WIDGETS.ScenarioUnassignedOrders) {
          dispatchPromises.push(dispatch(fetchUnassignedOrdersByScenario({ scenarioId: window.scenarioId, forceRefresh: true })))
        } else if (window.widgetType === TREE_WIDGETS.Inbox) {
          dispatchPromises.push(dispatch(fetchPlanningOrders({ orderIds: [] })))
        }
      }
    } else if (currentScreen === 'runsheet') {
      await dispatch(fetchRunsheets({ runsheetIds: [] }))

      // Pull historical runsheets prior to fetching orders
      for (const window of activeWidgetWindows) {
        if ((window.widgetType === TREE_WIDGETS.RunsheetShiftTable || window.widgetType === TREE_WIDGETS.RunsheetUnassignedOrders) && window.isHistorical) {
          historicalRunsheetDates.add(window.runsheetDate)
        }
      }

      const allHistoricalRunsheetDates = Array.from(historicalRunsheetDates)

      await Promise.allSettled(allHistoricalRunsheetDates.map(historicalDate => {
        return dispatch(fetchHistoricalRunsheets({
          date: historicalDate,
          dateRangeOption: 1,
        }))
      }))

      // Refresh active widget windows' data
      for (const window of activeWidgetWindows) {
        if (window.widgetType === TREE_WIDGETS.RunsheetShiftTable) {
          dispatchPromises.push(dispatch(fetchRunsheetOrdersByShift({ shiftId: window.shiftId, forceRefresh: true })))
        } else if (window.widgetType === TREE_WIDGETS.RunsheetUnassignedOrders) {
          dispatchPromises.push(dispatch(fetchUnassignedOrdersByRunsheet({ runsheetId: window.runsheetId, forceRefresh: true })))
        }
      }
    }

    // Wait for all dispatches to complete
    await Promise.all(dispatchPromises)
  },
)
